import { Select } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { InvoiceStateTag } from "@msuite/ui/tag";
import type { ClassNameProps, DataAttributeProps } from "@msuite/ui/types";
import { getProjectInvoiceStateString } from "@msuite/utils/projects";
import type { Enums } from "@msuite/utils/types";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

const invoicesStates: Enums<"outgoingInvoiceState">[] = ["paid", "overdue", "pending"];

/** Props Interface */
interface InvoicesStateSelectProps extends ClassNameProps, DataAttributeProps {
	defaultValue?: Enums<"outgoingInvoiceState"> | "all";
	onChange: (value: Enums<"outgoingInvoiceState"> | "all") => void;
	withAll?: boolean;
	label?: string;
}

export function InvoicesStateSelect({
	defaultValue,
	onChange,
	withAll,
	className,
	label,
	...props
}: InvoicesStateSelectProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm<{
		state: Enums<"outgoingInvoiceState"> | "all";
	}>({
		defaultValues: {
			state: defaultValue,
		},
	});

	/** Handlers */
	useEffect(() => {
		if (!defaultValue) return;
		methods.setValue("state", defaultValue);
	}, [defaultValue]);

	const allArray = withAll
		? [
				{
					item: {
						state: "all",
					},
					label: t("invoices.all_invoices"),
					value: "all",
				},
				"divider" as const,
			]
		: [];

	/** Render */
	return (
		<Select
			className={className}
			control={methods.control}
			name="state"
			isRequired={false}
			label={label}
			onChange={(value) => {
				onChange(value);
			}}
			options={allArray.concat(
				invoicesStates.map((state) => ({
					item: {
						state,
					},
					label: t(getProjectInvoiceStateString(state)),
					value: state,
				})),
			)}
			itemNotFoundLabel={t("general.not_found")}
			isNullable={false}
			renderOption={(item) => {
				if (item.state === "all") return <p>{t("invoices.all_invoices")}</p>;
				return <InvoiceStateTag state={item.state as Enums<"outgoingInvoiceState">} />;
			}}
			{...props}
		/>
	);
}
